import { useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.15 });

  const faqs = [
    {
      question: 'Como funciona o controle de ponto rural?',
      answer: 'Nosso sistema permite que trabalhadores rurais registrem ponto diretamente do campo usando dispositivos móveis. O sistema funciona offline e sincroniza automaticamente quando há conexão disponível.',
      icon: 'ri-smartphone-line',
      color: 'from-emerald-500 to-teal-500'
    },
    {
      question: 'Funciona sem internet?',
      answer: 'Sim! O sistema foi desenvolvido especialmente para o campo. Todos os registros são salvos localmente e sincronizados automaticamente quando houver conexão, garantindo que nenhum dado seja perdido.',
      icon: 'ri-wifi-off-line',
      color: 'from-teal-500 to-cyan-500'
    },
    {
      question: 'Como garante que não há fraude?',
      answer: 'Utilizamos geolocalização precisa, reconhecimento facial e validação de horários. Cada registro é verificado e armazenado de forma segura, impossibilitando alterações não autorizadas.',
      icon: 'ri-shield-check-line',
      color: 'from-cyan-500 to-blue-500'
    },
    {
      question: 'É difícil de usar?',
      answer: 'Não! O sistema foi projetado para ser extremamente simples. Com apenas alguns toques, o trabalhador registra entrada e saída. A interface é intuitiva e não requer treinamento complexo.',
      icon: 'ri-hand-heart-line',
      color: 'from-blue-500 to-indigo-500'
    },
    {
      question: 'Quanto tempo leva para implementar?',
      answer: 'A implementação é rápida. Em poucos dias sua equipe já estará usando o sistema. Fornecemos todo o suporte necessário para garantir uma transição suave e sem complicações.',
      icon: 'ri-time-line',
      color: 'from-indigo-500 to-purple-500'
    },
    {
      question: 'Qual o suporte oferecido?',
      answer: 'Oferecemos suporte técnico completo via telefone, e-mail e chat. Nossa equipe está disponível para ajudar com qualquer dúvida ou problema, garantindo que seu sistema funcione perfeitamente.',
      icon: 'ri-customer-service-2-line',
      color: 'from-purple-500 to-pink-500'
    }
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section 
      ref={ref}
      id="faq" 
      className={`py-20 lg:py-28 bg-gradient-to-br from-gray-50 via-teal-50/30 to-cyan-50/30 relative overflow-hidden transition-all duration-1000 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'
      }`}
    >
      {/* Elementos decorativos de fundo */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-20 right-10 w-72 h-72 bg-gradient-to-br from-teal-400 to-cyan-500 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 left-10 w-72 h-72 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      <div className="max-w-4xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16">
          <div className="inline-block mb-4 animate-swing-question">
            <span className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white px-6 py-2 rounded-full text-sm font-semibold shadow-lg">
              <i className="ri-question-answer-line mr-2"></i>
              Dúvidas Comuns
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            Perguntas Frequentes
          </h2>
          <p className="text-lg md:text-xl text-gray-700 max-w-2xl mx-auto leading-relaxed">
            Tire suas dúvidas sobre nosso sistema de controle de ponto rural
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              className={`group relative bg-white rounded-2xl overflow-hidden transition-all duration-300 cursor-pointer border-2 ${
                openIndex === index
                  ? 'shadow-2xl scale-105 border-transparent'
                  : hoveredIndex === index
                  ? 'shadow-xl scale-102 border-teal-200'
                  : 'shadow-md border-gray-100 hover:shadow-lg'
              }`}
            >
              {/* Gradiente de fundo animado */}
              <div className={`absolute inset-0 bg-gradient-to-br ${faq.color} opacity-0 transition-opacity duration-300 ${
                openIndex === index ? 'opacity-10' : hoveredIndex === index ? 'opacity-5' : ''
              }`}></div>

              {/* Elemento decorativo no canto */}
              <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${faq.color} opacity-0 rounded-full transform translate-x-16 -translate-y-16 transition-all duration-500 ${
                hoveredIndex === index || openIndex === index ? 'opacity-20 scale-150' : ''
              }`}></div>

              <button
                onClick={() => toggleFAQ(index)}
                className="relative w-full px-6 py-6 flex items-center gap-4 text-left"
              >
                {/* Ícone */}
                <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300 shadow-lg ${
                  openIndex === index
                    ? `bg-gradient-to-br ${faq.color} scale-110 rotate-6`
                    : hoveredIndex === index
                    ? `bg-gradient-to-br ${faq.color} scale-105`
                    : 'bg-gray-100'
                }`}>
                  <i className={`${faq.icon} text-xl transition-colors ${
                    openIndex === index || hoveredIndex === index ? 'text-white' : 'text-gray-600'
                  }`}></i>
                </div>

                {/* Pergunta */}
                <h3 className={`flex-1 text-base md:text-lg font-bold transition-colors ${
                  openIndex === index || hoveredIndex === index ? 'text-teal-600' : 'text-gray-900'
                }`}>
                  {faq.question}
                </h3>

                {/* Seta */}
                <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-300 ${
                  openIndex === index
                    ? `bg-gradient-to-br ${faq.color}`
                    : hoveredIndex === index
                    ? 'bg-teal-100'
                    : 'bg-gray-100'
                }`}>
                  <i className={`ri-arrow-down-s-line text-2xl transition-all duration-300 ${
                    openIndex === index ? 'rotate-180 text-white' : hoveredIndex === index ? 'text-teal-600' : 'text-gray-600'
                  }`}></i>
                </div>
              </button>

              {/* Resposta */}
              <div
                className={`overflow-hidden transition-all duration-500 ${
                  openIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="relative px-6 pb-6 pt-0">
                  <div className={`pl-16 pr-4 py-4 rounded-xl bg-gradient-to-br ${faq.color} bg-opacity-5 border-l-4 border-teal-500`}>
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </div>
                </div>
              </div>

              {/* Indicador de estado aberto */}
              {openIndex === index && (
                <div className={`absolute left-0 top-0 bottom-0 w-1.5 bg-gradient-to-b ${faq.color}`}></div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-16 text-center relative">
          <div className="inline-block bg-white rounded-2xl shadow-xl p-8 border-2 border-teal-100 hover:border-teal-300 transition-all duration-300 hover:shadow-2xl transform hover:scale-105">
            <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
              <i className="ri-chat-smile-3-line text-3xl text-white"></i>
            </div>
            <p className="text-lg font-semibold text-gray-900 mb-2">Ainda tem dúvidas?</p>
            <p className="text-sm text-gray-600 mb-6">Nossa equipe está pronta para ajudar você</p>
            <button
              onClick={() => {
                const element = document.getElementById('contato');
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              className="group inline-flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-teal-600 to-cyan-600 text-white font-semibold rounded-lg hover:from-teal-700 hover:to-cyan-700 transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              Entre em Contato
              <i className="ri-arrow-right-line text-lg group-hover:translate-x-1 transition-transform"></i>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
